// 二级单「付款计划」表格（ProTablePlusForm 两层 addGroup 主从结构，镜像 SolutionTable 范式）。
// 主行=付款节点（节点类型 Select + 时间复合列，mergeRowSpanKey 跨付款方式子行合并）；
// 子行=付款方式（付款类型 Select + 付款金额，可增删，addGroupKey 按付款节点分组）；
// 谈判视角（showNegotiationColumns）追加回复/意见反馈列（negotiation_items 回显）；
// 与费用预算（BudgetOfferTable）联动：付款类型可选项 = 费用预算已勾选类型（未勾选不出现，已存在行回显兜底），
//   LF/MG 金额汇总 = 预算金额，Royalty 占比合计 = 100%（validateFields 跨行汇总校验）；
// 底部统计行：预估总金额已分配 / 未分配（LF/MG 金额合计占预估总金额比例）；
// diff 态（compareEnabled + diffData）：数据列 RoundDiffCell（新值橙/旧值灰删除线）、行级 added/deleted 整行着色。

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import Decimal from 'decimal.js';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { useMemoizedFn } from 'ahooks';
import {
  Button,
  DatePicker,
  InputNumber,
  Select,
  Tag,
  Table,
} from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import { ProTablePlusForm } from '@mpc/common/src/components/ProTablePlusForm';
import type { AddGroupFormColumn } from '@mpc/common/src/components/ProTablePlusForm/addGroupTable';
import CurrencyInput from '@mpc/common/src/components/SForm/SFormElement/CurrencyInput';
import CurrencyType from '@mpc/common/src/components/SForm/SFormElement/CurrencyType';
import SInputPercent from '@mpc/common/src/components/SForm/SFormElement/InputPercent';
import { enums, type IpCooperationTriggerDateTypeEnum } from 'services/api';
import type {
  NegotiationEditState,
  NegotiationItem,
} from '@mpc/common/src/features/IPCooperationDemand/NegotiationTable';
import {
  ActionCell,
  ACTION_TAG_COLOR,
  ACTION_TEXT_MAP,
  createRenderBatchEditTitle,
  OpinionCell,
} from '@mpc/common/src/features/IPCooperationDemand/NegotiationTable';
import { getOpinionTitle } from '@mpc/common/src/features/IPCooperationDemand/NegotiationTable/renderCells';
import RoundDiffCell, {
  DIFF_TEXT_CLASS,
  OLD_VALUE_CLASS,
} from '@mpc/common/src/features/IPCooperationDemand/RoundDiff/RoundDiffCell';
import ActionDiffCell from '@mpc/common/src/features/IPCooperationDemand/RoundDiff/ActionDiffCell';
import type {
  PaymentPlanDiffMap,
  PaymentPlanRowDiff,
} from '@mpc/common/src/features/IPCooperationDemand/RoundDiff/types';
import { PAYMENT_PLAN_ITEM_KEY } from '@mpc/common/src/features/IPCooperationDemand/utils';
import { currencyEnum } from '@mpc/common/src/utils/number';

/** 付款类型（value 对齐 BudgetOfferTable BUDGET_ROWS item_key：lf/mg/royalty） */
export const PAYMENT_TYPE_LF = 'lf';
export const PAYMENT_TYPE_MG = 'mg';
export const PAYMENT_TYPE_ROYALTY = 'royalty';

/** 付款类型选项（授权金 LF / 保底金 MG / 分成 Royalty） */
export const PAYMENT_TYPE_OPTIONS = [
  { value: PAYMENT_TYPE_LF, name: '授权金（LF）' },
  { value: PAYMENT_TYPE_MG, name: '保底金（MG）' },
  { value: PAYMENT_TYPE_ROYALTY, name: '分成（Royalty）' },
];

/** 付款节点时间单位（对齐后端 IpCooperationOffsetUnitEnum：WORKDAY=工作日 / NATURAL_DAY=自然日） */
export type PaymentTimeUnit = 'WORKDAY' | 'NATURAL_DAY';

/** 付款节点时间单位选项（days 类型：工作日/自然日） */
export const PAYMENT_TIME_UNIT_OPTIONS: {
  value: PaymentTimeUnit;
  name: string;
}[] = [
  { value: 'WORKDAY', name: '工作日' },
  { value: 'NATURAL_DAY', name: '自然日' },
];

/** 付款节点复合值（节点类型 + 时间；字段 key 对齐接口 TriggerCondition，前端不自定义重命名；对齐 DurationInput 复合控件对象 value 范式） */
export interface PaymentNodeValue {
  /** 触发条件主数据 code（对齐接口 trigger_condition.code） */
  code?: string;
  /** 节点名称快照（对齐接口 trigger_condition.name；编辑/回显时写入，只读展示优先读，不依赖选项接口） */
  name?: string;
  /** 日期类型（OFFSET=日期偏移 / FIXED=具体日期，对齐接口 trigger_condition.date_type） */
  date_type?: IpCooperationTriggerDateTypeEnum;
  /** 偏移数值（date_type=OFFSET 时，对齐接口 trigger_condition.offset_value） */
  offset_value?: number;
  /** 偏移单位（date_type=OFFSET 时，对齐接口 trigger_condition.offset_unit；对齐后端 IpCooperationOffsetUnitEnum） */
  offset_unit?: PaymentTimeUnit;
  /** 固定日期（date_type=FIXED 时，对齐接口 trigger_condition.fixed_date，string YYYY-MM-DD；DatePicker format 输出） */
  fixed_date?: string;
}

/** 付款节点类型选项（来源：v2IpCooperationCommonQueryPaymentTriggerOptions 接口，date_type 直接透传服务端 date_type；缺省由组件内部加载，paymentNodeOptions prop 可覆盖） */
export interface PaymentNodeOption {
  value: string;
  name: string;
  /** 服务端触发条件日期类型（直接透传 date_type：OFFSET=日期偏移 / FIXED=具体日期，不再前端映射 offset_value/fixed_date） */
  date_type?: string;
  units?: { value: PaymentTimeUnit; name: string }[];
}

/** 费用预算联动配置（BudgetOfferTable 勾选态 + 金额） */
export interface PaymentBudgetConfig {
  lfEnabled?: boolean;
  mgEnabled?: boolean;
  royaltyEnabled?: boolean;
  lfAmount?: number;
  mgAmount?: number;
}

/** 付款计划行（平铺，子行继承父行节点/回复/意见字段） */
export interface PaymentPlanRow {
  /** 付款节点分组键（主行，trigger_condition 内容 md5，buildPaymentPlanTableRows 生成；diff 按节点分组） */
  payment_node_key?: string;
  /** 付款方式子行分组键（uuid） */
  payment_type_key?: string;
  /** 付款节点复合值（节点类型 + 时间） */
  payment_node?: PaymentNodeValue;
  /** 付款类型（lf/mg/royalty） */
  payment_type?: string;
  /** 付款金额（LF/MG 金额；Royalty 分成占比） */
  payment_amount?: number;
  /** 币种（LF/MG，默认人民币） */
  payment_currency?: string;
  /** 谈判条款标识（payment_plan section item_key，buildPaymentPlanTableRows 注入）：外包谈判视角回复/意见列经 ActionCell/OpinionCell 读 editState['payment_plan'] */
  item_key?: string;
  /** 版权方回复（谈判条款扁平注入行字段） */
  action?: string;
  /** 版权方意见（谈判条款扁平注入行字段） */
  opinion?: string;
  /** 新增占位行标记（ProTablePlusForm addGroup 内部） */
  $addRow?: string[];
  /** 对比态 diff 子行序号（roundDiff.ts 注入，diff 键=${payment_node_key}_${$diffIndex}；不依赖 ProTablePlusForm 内部 l$index） */
  $diffIndex?: number;
}

export interface PaymentPlanTableProps {
  /** 谈判条款列表（negotiation_items，回复/意见只读回显兜底数据源） */
  items?: NegotiationItem[];
  /** 受控表格数据（payment_plan 字段值） */
  value?: PaymentPlanRow[];
  /** 表格数据变更回调（写回 payment_plan 字段） */
  onChange?: (next: PaymentPlanRow[]) => void;
  /** 是否可编辑（增删行 + 单元格编辑；只读态纯展示） */
  editable?: boolean;
  /** 场景模式：external=外包（付款计划数据列只读 + 回复/意见可编辑，ActionCell/OpinionCell）；internal/缺省=内包（数据列可编辑 + 回复/意见只读） */
  mode?: 'internal' | 'external';
  /** 谈判视角：追加回复/意见反馈列（仅谈判阶段回显版权方操作） */
  showNegotiationColumns?: boolean;
  /** 编辑状态（mode='external'；key=PAYMENT_PLAN_ITEM_KEY，对齐 BudgetOfferTable external 模式） */
  editState?: Record<string, NegotiationEditState>;
  /** 单条编辑变更回调 */
  onEditChange?: (
    itemKey: string,
    patch: Partial<NegotiationEditState>
  ) => void;
  /** 批量操作（mode='external' 时回复列表头批量编辑 Popover，对齐 CoreTermTable） */
  onBatchAction?: (action: string, itemKeys?: string[]) => void;
  /** 费用预算联动配置（付款类型可选项过滤 + 金额汇总校验） */
  budgetConfig?: PaymentBudgetConfig;
  /** 付款节点类型选项（服务端 fieldoptions 接口未 ready，缺省 mock） */
  paymentNodeOptions?: PaymentNodeOption[];
  /** 币种（费用预算预算总金额币种，对齐项目 currencyEnum 小写键 / CurrencyEnum 大写类型；付款金额列同步展示且不可修改） */
  currency?: string;
  /** 对比态开关：仅 compareEnabled=true 时展示 diff 样式（缺省 false） */
  compareEnabled?: boolean;
  /** 历史轮次对比 diff（key=${payment_node_key}_${子行序号 l2$index} → 各字段新旧值；payment_type_key 为每次渲染重新生成的 uuid，不作 diff 键，roundDiff.ts 按节点 md5 + 子行序号生成） */
  diffData?: PaymentPlanDiffMap;
}

/** PaymentPlanTable 校验能力（SFormItem watchChildFormValidate 提交时调用） */
export interface PaymentPlanTableRef {
  validateFields: () => Promise<unknown>;
}

/** 付款节点只读格式化（两行：节点名 / 时间；days → 数量单位，date → YYYY-MM-DD） */
function formatPaymentNode(
  value?: PaymentNodeValue,
  options?: PaymentNodeOption[],
  // 时间行类名：缺省灰（text-color-3）；diff 着色场景传 '' 不设类，继承外层 diff 色（绿/橙/灰删除线）
  timeClassName = 'text-color-3'
): React.ReactNode {
  if (!value?.code) return '--';
  const opts = options ?? [];
  const opt = opts.find((o) => o.value === value.code);
  const date_type = opt?.date_type ?? value?.date_type;
  // 名称优先读行内快照（value.name，编辑/回显写入），次选选项匹配，再回退 code
  const nodeName = value?.name ?? opt?.name ?? value.code;
  let timeText = '';
  if (
    date_type === enums.IpCooperationTriggerDateTypeEnum.FIXED &&
    value.fixed_date
  ) {
    timeText = dayjs(value.fixed_date).format('YYYY-MM-DD');
  } else if (value.offset_value != null && value.offset_unit) {
    const unitName =
      (opt?.units ?? PAYMENT_TIME_UNIT_OPTIONS).find(
        (o) => o.value === value.offset_unit
      )?.name ?? value.offset_unit;
    timeText = `${value.offset_value}${unitName}`;
  }
  if (!timeText) return nodeName;
  return (
    <span className="inline-flex flex-col leading-[22px]">
      <span>{nodeName}</span>
      <span className={timeClassName || undefined}>时间：{timeText}</span>
    </span>
  );
}

/** 币种显示名（用项目 currencyEnum 小写键映射，大小写兼容：currencytype 组件输出小写 code、接口/历史数据大写，映射前 toLowerCase） */
function currencyName(currency?: string): string {
  if (!currency) return '元';
  const key = currency.toLowerCase();
  return currencyEnum[key] ?? currency;
}

/** 付款金额只读格式化（Royalty → 百分比；LF/MG → 金额 + 币种，币种同步费用预算） */
function formatPaymentAmount(
  value?: number,
  type?: string,
  currency?: string
): string {
  if (value == null) return '--';
  // 分成占比 form 存小数（0.15=15%），只读展示转百分比（×100，Decimal 避免浮点误差）
  if (type === PAYMENT_TYPE_ROYALTY)
    return `${new Decimal(value)
      .mul(100)
      .toFixed(2)
      .replace(/\.?0+$/, '')}%`;
  return `${Number(value).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyName(currency)}`;
}

/** 当前行对应的 diff 记录（键=${payment_node_key}_${$diffIndex}，roundDiff.ts 注入） */
function getRowDiff(
  row: PaymentPlanRow | undefined,
  diffData?: PaymentPlanDiffMap
): PaymentPlanRowDiff | undefined {
  if (!diffData || !row?.payment_node_key) return undefined;
  return diffData[`${row.payment_node_key}_${row.$diffIndex}`];
}

/** 统一 diff 行渲染：行级 added/deleted 整行着色 + 字段新旧对比 RoundDiffCell + 无 diff 原样渲染（付款节点/类型/金额三数据列共用） */
function renderDiffValue(options: {
  rd?: PaymentPlanRowDiff;
  value: unknown;
  renderValue: (v: unknown) => React.ReactNode;
  diffKey: 'paymentNode' | 'paymentType' | 'paymentAmount';
}): React.ReactNode {
  const { rd, value, renderValue, diffKey } = options;
  if (rd?.rowType === 'added') {
    return <span className={DIFF_TEXT_CLASS.added}>{renderValue(value)}</span>;
  }
  if (rd?.rowType === 'deleted') {
    return <span className={OLD_VALUE_CLASS}>{renderValue(value)}</span>;
  }
  const diff = rd?.[diffKey];
  if (diff) {
    return (
      <RoundDiffCell
        diff={diff}
        renderNewValue={renderValue}
        renderOldValue={renderValue}
      />
    );
  }
  return renderValue(value);
}

/** 付款节点序号（镜像 inner nodeOrderMap：按 value 全量出现顺序编号，与序号列展示一致，供报错定位行） */
function buildNodeOrderMap(rows: PaymentPlanRow[]): Record<string, number> {
  const nodeOrderMap: Record<string, number> = {};
  let order = 0;
  rows.forEach((r) => {
    const key = r.payment_node_key;
    if (key && nodeOrderMap[key] == null) nodeOrderMap[key] = ++order;
  });
  return nodeOrderMap;
}

/** 统计列及所有嵌套子列的叶子列总数（树形表格 summary 的 colSpan 需要；无 children 的列计 1） */
function countLeafColumns(cols: AddGroupFormColumn[]): number {
  return cols.reduce(
    (sum, col) =>
      sum + (col.children?.length ? countLeafColumns(col.children) : 1),
    0
  );
}

/** 结构校验：至少一行付款计划（付款节点主行）+ 每个付款节点下至少一行付款方式子行；通过返回 null，失败返回报错文案 */
function validateStructure(
  rows: PaymentPlanRow[],
  nodeOrderMap: Record<string, number>
): string | null {
  if (!rows.length) {
    return '请至少添加一行付款计划';
  }
  const nodeNo = (key?: string) => nodeOrderMap[key ?? ''] ?? '?';
  // 结构校验：每个付款节点下至少一行付款方式子行（主行只有 payment_node_key、子行才有 payment_type_key；
  // 占位子行（$addRow）已过滤，节点下无真实子行即拦截「仅新增节点未填付款方式」）
  const nodeKeys = Array.from(
    new Set(rows.map((r) => r.payment_node_key).filter(Boolean))
  );
  for (const nodeKey of nodeKeys) {
    const hasChild = rows.some(
      (r) => r.payment_node_key === nodeKey && r.payment_type_key
    );
    if (!hasChild) {
      return `第 ${nodeNo(nodeKey)} 个付款节点下至少需要一行付款方式`;
    }
  }
  return null;
}

/** 同节点付款类型去重校验（每种付款类型每付款节点仅一次）：通过返回 null，失败返回报错文案 */
function validateDuplicateType(
  rows: PaymentPlanRow[],
  nodeOrderMap: Record<string, number>
): string | null {
  const nodeNo = (key?: string) => nodeOrderMap[key ?? ''] ?? '?';
  const typeName = (type?: string) =>
    PAYMENT_TYPE_OPTIONS.find((o) => o.value === type)?.name ?? type ?? '';
  const seenByNode: Record<string, Set<string>> = {};
  for (const r of rows) {
    const key = r.payment_node_key;
    const type = r.payment_type;
    if (!key || !type) continue;
    if (!seenByNode[key]) seenByNode[key] = new Set();
    if (seenByNode[key].has(type)) {
      return `第 ${nodeNo(key)} 个付款节点下付款类型「${typeName(type)}」只能出现一次`;
    }
    seenByNode[key].add(type);
  }
  return null;
}

/** 跨行汇总校验（费用预算联动；未勾选类型跳过）：通过返回 null，失败返回报错文案 */
function validateBudgetSum(
  rows: PaymentPlanRow[],
  budgetConfig?: PaymentBudgetConfig
): string | null {
  const sumByType = (type: string) => {
    let s = new Decimal(0);
    rows.forEach((r) => {
      if (r.payment_type === type) s = s.plus(Number(r.payment_amount) || 0);
    });
    return s;
  };

  if (
    budgetConfig?.lfEnabled &&
    !sumByType(PAYMENT_TYPE_LF).eq(budgetConfig.lfAmount ?? 0)
  ) {
    return '付款计划中授权金（LF）金额合计需与费用预算一致';
  }
  if (
    budgetConfig?.mgEnabled &&
    !sumByType(PAYMENT_TYPE_MG).eq(budgetConfig.mgAmount ?? 0)
  ) {
    return '付款计划中保底金（MG）金额合计需与费用预算一致';
  }
  if (
    budgetConfig?.royaltyEnabled &&
    // 分成占比 form 存小数（0.15=15%），合计需为 1（=100%）
    !sumByType(PAYMENT_TYPE_ROYALTY).eq(1)
  ) {
    return '付款计划中分成占比合计需为100%';
  }
  return null;
}

/** 付款节点复合编辑控件（两行：节点类型 Select 一行 + 时间一行；days → InputNumber+单位 Select；date → DatePicker） */
const PaymentNodeInput: React.FC<{
  value?: PaymentNodeValue;
  onChange?: (v?: PaymentNodeValue) => void;
  paymentNodeOptions?: PaymentNodeOption[];
  /** 只读态（审批节点/对比态）：纯文本展示节点名+时间，不渲染输入控件 */
  editable?: boolean;
}> = ({ value = {}, onChange, paymentNodeOptions = [], editable = true }) => {
  // 只读态：复用 formatPaymentNode 只读两行文本（节点名黑 + 时间灰小字，对齐只读设计稿），不渲染 Select/输入控件
  if (!editable) return formatPaymentNode(value, paymentNodeOptions);
  const opt = paymentNodeOptions.find((o) => o.value === value?.code);
  // isDays = date_type !== FIXED（含 OFFSET/undefined 均走数量+单位输入，保持旧 timeType='days' 语义）
  const isDays =
    opt?.date_type !== enums.IpCooperationTriggerDateTypeEnum.FIXED;
  return (
    <div className="flex flex-col gap-1">
      {/* 节点 Select 用 className w-full 撑满（对齐 IpResourceSelect 模式）；第二行 flex-1 均分，两行与单元格同宽对齐 */}
      <Select
        value={value?.code}
        onChange={(code) => {
          // 选中节点时同步写入名称快照 + date_type（对齐接口 TriggerCondition.name/date_type，供提交上送/只读展示）
          const opt = paymentNodeOptions.find((o) => o.value === code);
          onChange?.({
            ...value,
            code,
            name: opt?.name,
            date_type: opt?.date_type as
              | IpCooperationTriggerDateTypeEnum
              | undefined,
          });
        }}
        options={paymentNodeOptions}
        placeholder="请选择付款节点"
        className="w-full"
      />
      {opt && isDays && (
        <div className="flex w-full gap-1">
          <InputNumber
            value={value?.offset_value}
            onChange={(offsetValue) =>
              onChange?.({
                ...value,
                offset_value: offsetValue ?? undefined,
              })
            }
            min={0}
            precision={0}
            placeholder="请输入"
            direction="vertical"
            className="min-w-0 flex-1"
          />
          <Select
            value={value?.offset_unit}
            onChange={(offsetUnit) =>
              onChange?.({
                ...value,
                offset_unit: offsetUnit as PaymentTimeUnit,
              })
            }
            options={PAYMENT_TIME_UNIT_OPTIONS}
            className="min-w-[100px] flex-1"
          />
        </div>
      )}
      {opt && !isDays && (
        <DatePicker
          value={value?.fixed_date}
          format="YYYY-MM-DD"
          // onChange 第二参 formatString 为格式化字符串，存入 fixed_date 字段使表单值统一为 string（对齐接口 fixed_date），空值存 undefined
          onChange={(date, formatString) =>
            onChange?.({ ...value, fixed_date: formatString || undefined })
          }
          placeholder="请选择日期"
          className="w-full"
        />
      )}
    </div>
  );
};

/** 付款金额编辑控件：Royalty → 百分比输入框（SInputPercent，form/接口存小数 0.15=15%、展示百分比）；LF/MG → InputNumber + 币种（同步费用预算预算总金额，只读不可改） */
const PaymentAmountInput: React.FC<{
  value?: number;
  onChange?: (v?: number) => void;
  row?: PaymentPlanRow;
  currency?: string;
  /** 只读态（审批节点/对比态）：LF/MG 用 CurrencyType 展示金额+币种（对齐 IP 表单金额只读范式），Royalty 保持百分比文本 */
  editable?: boolean;
}> = ({ value, onChange, row, currency, editable = true }) => {
  const isRoyalty = row?.payment_type === PAYMENT_TYPE_ROYALTY;
  // 只读态：LF/MG → CurrencyType view（金额千分位 + 币种 + 数量级标签，对齐只读设计稿）；Royalty → 百分比文本
  if (!editable) {
    if (isRoyalty) {
      return (
        <span>{formatPaymentAmount(value, row?.payment_type, currency)}</span>
      );
    }
    return <CurrencyType view value={{ value: value ?? '', currency }} />;
  }
  // 分成（Royalty）：百分比输入框，value 传小数（0.15）、onChange 返回小数（SInputPercent valueType=number 失焦 ÷100 回写）；
  // 输入框下方追加提示：分成占比语义 = 该付款节点付款金额占需付款分成总金额的百分比（text-color-3 小字，对齐 inputExtra 范式）
  if (isRoyalty) {
    return (
      <div className="flex flex-col gap-1">
        <SInputPercent
          value={value}
          onChange={(v) => {
            onChange?.(v == null ? undefined : Number(v));
          }}
          min={0}
          max={1}
          digits={2}
          valueType="number"
          placeholder="请输入"
          style={{ width: '100%' }}
        />
        <div className="text-xs text-color-3">
          此处指该付款节点付款金额占需付款分成总金额的百分比
        </div>
      </div>
    );
  }
  return (
    <CurrencyInput
      value={{ value, currency }}
      onChange={(next) => {
        // 只取金额写回扁平 number（custom-component 列 onChange 返回值整体写入 row[dataIndex]，
        // 不能把 {value, currency} 对象落进 payment_amount，否则破坏后端契约/汇总校验/必填校验）；
        // 空串防护：Number('')=0 会让必填校验漏检，空输入须回 undefined
        const v = next?.value;
        onChange?.(v == null || v === '' ? undefined : Number(v));
      }}
      precision={2}
      showCurrency
      showUnitIndicator
      // 币种同步费用预算预算总金额（Select 锁定不可改，金额 InputNumber 仍可编辑；镜像 InsuranceAmountTable 金额（谈判币种）范式）
      selectProps={{ disabled: true }}
      inputProps={{ min: 0.01, placeholder: '请输入' }}
    />
  );
};

// SFormItem watchChildFormValidate 会注入 ref 并调用 validateFields 联动校验；
// forwardRef 把校验能力转发到内部 ProTablePlusForm（列必填）+ 跨行汇总校验（费用预算联动）
const PaymentPlanTable = forwardRef<PaymentPlanTableRef, PaymentPlanTableProps>(
  (props, ref) => {
    const tableRef = useRef(null);
    // 最新 props 引用：validateFields 闭包经 ref 读最新值，避免把 value/budgetConfig 加入依赖导致 ref 频繁重建
    const propsRef = useRef(props);
    propsRef.current = props;
    useImperativeHandle(
      ref,
      () => ({
        validateFields: () => {
          const { budgetConfig, value } = propsRef.current;
          if (!tableRef.current?.validateFields) return Promise.resolve();
          return tableRef.current
            .validateFields()
            .then(() => {
              // 结构校验：至少一行付款计划（付款节点主行）+ 每节点至少一行付款方式子行
              const rows = (value ?? []).filter((r) => !r.$addRow);
              const nodeOrderMap = buildNodeOrderMap(value ?? []);
              const structureError = validateStructure(rows, nodeOrderMap);
              if (structureError) return Promise.reject(structureError);
              // 同节点付款类型去重校验（每种付款类型每付款节点仅一次；拦截历史数据/绕过 UI 的重复）
              const duplicateError = validateDuplicateType(rows, nodeOrderMap);
              if (duplicateError) return Promise.reject(duplicateError);
              // 单元格必填校验（付款节点复合值 / 付款金额）由各列 rule 处理：custom-component 列 rule 经上方
              // validateFieldsAndScroll 委托在提交时触发（镜像 SolutionTable 范式），此处不再重复校验
              // 跨行汇总校验（费用预算联动；未勾选类型跳过）
              const budgetError = validateBudgetSum(rows, budgetConfig);
              if (budgetError) return Promise.reject(budgetError);
              return Promise.resolve();
            })
            .catch((error: any) => Promise.reject(error?.message ?? error));
        },
      }),
      []
    );
    const {
      items = [],
      value = [],
      onChange,
      editable = false,
      mode,
      showNegotiationColumns = false,
      editState = {},
      onEditChange,
      onBatchAction,
      budgetConfig,
      paymentNodeOptions,
      currency,
      compareEnabled,
      diffData,
    } = props;
    // 场景编辑开关：mode 决定哪类列可编辑（编辑态仍需 editable 为真；镜像 SolutionTable isInternalEdit/isExternalEdit）
    //  - internal（含缺省）：内包，付款节点/类型/金额 + 新增删除行可编辑
    //  - external：外包，仅回复/意见可编辑（数据列恒只读，外包版权方报价只读）
    const isInternalEdit = (mode == null || mode === 'internal') && editable;
    const isExternalEdit = mode === 'external' && editable;

    // 对比态开关门控：仅 compareEnabled=true 时展示 diff 样式
    const activeDiffData = compareEnabled ? diffData : undefined;
    // 最新受控 value 引用：render 闭包经 ref 读最新行数，避免把 value 加入 columns 依赖导致每次编辑重建列
    const valueRef = useRef(value);
    valueRef.current = value;

    useEffect(() => {
      if (compareEnabled || mode === 'external') {
        tableRef.current?.treeInitData?.(value);
      }
    }, [compareEnabled, value]);

    // 详情条款按 item_key 索引（回复/意见只读回显兜底）
    const itemsByKey = useMemo(() => {
      const map: Record<string, NegotiationItem> = {};
      (items || []).forEach((i) => {
        if (i.item_key) map[i.item_key] = i;
      });
      return map;
    }, [items]);

    // 共享单元格渲染上下文（回复/意见列 external 编辑 + 批量编辑标题；对齐 SolutionTable cellCtx 范式）
    const cellCtx = {
      itemsByKey,
      editState,
      // 回复/意见批量编辑仅外包谈判场景可用（内包商务修改只读展示版权方回复）
      editable: isExternalEdit,
      onEditChange,
    };
    const renderBatchEditTitle = createRenderBatchEditTitle(cellCtx);

    // 付款类型基础可选项：费用预算勾选态过滤（未勾选不出现）；未接 budgetConfig 时全量。
    // 不再把已存在行类型全局回显兜底——费用预算取消勾选后该类型不应再被其它行选择；
    // 行自身已选类型的展示兜底由 getRowPaymentTypeOptions 按行补齐
    const basePaymentTypeOptions = useMemo(() => {
      const enabled = new Set<string>();
      if (!budgetConfig || budgetConfig.lfEnabled) enabled.add(PAYMENT_TYPE_LF);
      if (!budgetConfig || budgetConfig.mgEnabled) enabled.add(PAYMENT_TYPE_MG);
      if (!budgetConfig || budgetConfig.royaltyEnabled)
        enabled.add(PAYMENT_TYPE_ROYALTY);
      return PAYMENT_TYPE_OPTIONS.filter((o) => enabled.has(o.value));
    }, [budgetConfig]);

    // 删除整个付款节点（主行）：treeDeleteRowById 按 treeMap key（= payment_node_key）删除主行节点 + 全部子行（delete 递归删 childrenId）；
    // 新增节点（仅子行 addRow 占位）同样可删
    const handleDeleteNode = useMemoizedFn((nodeKey?: string) => {
      if (!nodeKey) return;
      tableRef.current?.treeDeleteRowById(nodeKey);
    });

    // ===== 列构建函数 =====

    // 新增行按钮（summary「新增」按钮；onAdd 决定 treeAddToParent 目标/行数据）
    const renderAddRowButton = (onAdd: () => void) => (
      <Button
        type="text"
        icon={<Plus />}
        className="text-[#1677ff]"
        onClick={onAdd}
      >
        新增
      </Button>
    );
    // 新增付款节点主行：treeAddToParent 到 root（level0），子行占位由 addGroupTable handleAddRowCell 自动补
    const renderAddNodeButton = (r: { item_key?: string }) =>
      renderAddRowButton(() => {
        // nodeIdKey 用子行 mergeRowSpanKey 末 key（payment_type_key）：addToParent 内部 set(newItem,'payment_type_key',newId)
        // 使新增行字段与 treeMap key 一致，treeDeleteRowById/编辑回写才能匹配（传 '__row_key__' 会 key 失配导致删除无效/新增错乱）
        tableRef.current?.treeAddToParent(
          'payment_node_key',
          {
            payment_node_key: uuidv4(),
            payment_type_key: uuidv4(),
            item_key: r.item_key,
          },
          r.item_key
        );
      });
    // 新增付款方式子行（付款类型 + 付款金额，挂到当前付款节点主行）
    const renderAddTypeButton = (r: any) =>
      renderAddRowButton(() => {
        // nodeIdKey 用子行 mergeRowSpanKey 末 key（payment_type_key）：addToParent 内部 set(newItem,'payment_type_key',newId)
        // 使新增行字段与 treeMap key 一致，treeDeleteRowById/编辑回写才能匹配（传 '__row_key__' 会 key 失配导致删除无效/新增错乱）
        tableRef.current?.treeAddToParent(
          'payment_type_key',
          {
            payment_type_key: uuidv4(),
            item_key: r.item_key,
          },
          r.payment_node_key
        );
      });

    // 序号列（主行维度，按节点出现顺序编号）
    const indexColumn: AddGroupFormColumn = {
      dataIndex: 'index_no',
      title: '序号',
      width: '60px',
      mergeRowSpanKey: ['item_key', 'payment_node_key'],
      render: (value, row, index) => {
        const content = row?.l1$index + 1 ?? '--';
        if (row?.rowType === 'added') {
          return <span className={DIFF_TEXT_CLASS.added}>{content}</span>;
        }
        if (row?.rowType === 'deleted') {
          return <span className={OLD_VALUE_CLASS}>{content}</span>;
        }
        return content;
      },
    };

    // 付款节点列（主行复合列：节点类型 Select + 时间；仅内包编辑态可编辑，付款节点/付款类型/付款金额三列必填，对齐设计稿红 *）
    let nodeEditRender: AddGroupFormColumn['editRender'] = undefined;
    if (isInternalEdit) {
      nodeEditRender = ({ row }) => ({
        name: 'custom-component',
        editable: isInternalEdit,
        props: {
          customComp: PaymentNodeInput,
          paymentNodeOptions,
        },
        // 付款节点列复合值必填校验：节点类型 + 时间（days→数量/单位，date→日期）；
        // required 对对象恒通过，须 validator 逐字段校验；custom-component 列 rule 经外层
        // validateFieldsAndScroll 委托在提交时触发（镜像 SolutionTable「仅内包编辑态配 rule + callback」范式）
        rule: [
          {
            validator: (
              _rule,
              value: PaymentNodeValue,
              callback: (error?: string) => void
            ) => {
              if (!value?.code) {
                callback('请选择付款节点');
                return;
              }
              const opt = paymentNodeOptions.find(
                (o) => o.value === value.code
              );
              if (
                opt?.date_type !== enums.IpCooperationTriggerDateTypeEnum.FIXED
              ) {
                if (value.offset_value == null || value.offset_value === '') {
                  callback('请填写付款节点时间数量');
                  return;
                }
                if (!value.offset_unit) {
                  callback('请选择付款节点时间单位');
                  return;
                }
              } else {
                if (!value.fixed_date) {
                  callback('请选择付款节点日期');
                  return;
                }
              }
              callback();
            },
          },
        ],
      });
    }
    const nodeColumn: AddGroupFormColumn = {
      dataIndex: 'payment_node',
      title: '付款节点',
      // 内包只读态收窄到 220（序号60+节点220+类型170+金额190=640，W=1140），使回复/意见列与权益表格对齐；
      // 外包保持 240，仅内包区分（节点编辑态/外包恒 240）。
      width: mode === 'external' ? '240px' : isInternalEdit ? '240px' : '220px',
      mergeRowSpanKey: ['item_key', 'payment_node_key'],
      editRender: nodeEditRender,
      render: (value, row, index) => {
        const rd = getRowDiff(row, activeDiffData);
        // 时间行类名：正常态灰；diff 着色态传 '' 不设类，继承外层 diff 色（修复时间行被 text-color-3 覆盖不着色）
        const renderNode = (v: unknown, timeClass?: string) =>
          formatPaymentNode(
            v as PaymentNodeValue,
            paymentNodeOptions,
            timeClass
          );
        if (rd?.rowType === 'added') {
          return (
            <span className={DIFF_TEXT_CLASS.added}>
              {renderNode(value, '')}
            </span>
          );
        }
        if (rd?.rowType === 'deleted') {
          return (
            <span className={OLD_VALUE_CLASS}>{renderNode(value, '')}</span>
          );
        }
        if (rd?.paymentNode) {
          return (
            <RoundDiffCell
              diff={rd.paymentNode}
              renderNewValue={(v) => renderNode(v, '')}
              renderOldValue={(v) => renderNode(v, '')}
            />
          );
        }
        return renderNode(value, 'text-color-3');
      },
    };

    // 付款类型列（addGroup 子表）
    const typeColumn: AddGroupFormColumn = {
      dataIndex: 'payment_type',
      title: '付款类型',
      width: 170,
      mergeRowSpanKey: ['item_key', 'payment_node_key', 'payment_type_key'],
      editRender: ({ row }) => ({
        name: 'select',
        editable: isInternalEdit,
        props: {
          options: basePaymentTypeOptions,
          placeholder: '请选择',
          size: 'middle',
        },
        rule: [
          { required: true, message: '请选择付款类型' },
          {
            validator: (_rule, value, callback) => {
              if (!value) {
                callback();
                return;
              }
              // 同付款节点其它子行已占用该类型 → 行内即时拦截（镜像 nodeEditRender validator 范式）
              const duplicated = valueRef.current.some(
                (r) =>
                  !r.$addRow &&
                  r.payment_node_key === row?.payment_node_key &&
                  r.payment_type_key !== row?.payment_type_key &&
                  r.payment_type === value
              );
              if (duplicated) {
                callback(
                  `同付款节点下付款类型「${
                    PAYMENT_TYPE_OPTIONS.find((o) => o.value === value)?.name ??
                    value
                  }」只能出现一次`
                );
                return;
              }
              callback();
            },
          },
        ],
      }),
      render: (value, row, index) => {
        const renderType = (v: unknown) => (
          <span>
            {PAYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.name || v || '--'}
          </span>
        );
        return renderDiffValue({
          rd: getRowDiff(row, activeDiffData),
          value,
          renderValue: renderType,
          diffKey: 'paymentType',
        });
      },
    };

    // 付款金额（LF/MG）CurrencyType 只读渲染：isOld=历史值灰删除线（对齐 BudgetOfferTable renderBudgetMoneyDiff 范式）
    const renderMoneyDiff = (isOld: boolean) => (v: unknown) => (
      <CurrencyType
        view
        value={{ value: (v ?? '') as number, currency }}
        currencyColor={isOld ? 'grey' : undefined}
        currencyStyle={{ textDecoration: isOld ? 'line-through' : undefined }}
        textStyle={{ textDecoration: isOld ? 'line-through' : undefined }}
      />
    );

    // 付款金额列（addGroup 子表；仅内包编辑态配 rule，镜像 SolutionTable「仅编辑态配 rule」范式）
    let amountEditRender: AddGroupFormColumn['editRender'] = undefined;
    if (isInternalEdit) {
      amountEditRender = ({ row }) => ({
        name: 'custom-component',
        editable: isInternalEdit,
        props: {
          customComp: PaymentAmountInput,
          row,
          currency,
        },
        rule: [{ required: true, message: '请输入付款金额' }],
      });
    }
    const amountColumn: AddGroupFormColumn = {
      dataIndex: 'payment_amount',
      title: '付款金额/分成付款比例',
      // 外包内容列总宽统一 700（序号60+付款节点240+付款类型170+付款金额230，对齐上线联动明细/授权信息/权益三表回复列 x）
      // 内包只读态收窄到 190（配合付款节点 220，合计 640；编辑态保持 250）
      width: mode === 'external' ? 230 : isInternalEdit ? 250 : 190,
      mergeRowSpanKey: ['item_key', 'payment_node_key', 'payment_type_key'],
      editRender: amountEditRender,
      render: (value, row, index) => {
        // 分成占比（Royalty）→ 百分比文本（form 存小数 0.15=15%，非金额语义，不走 CurrencyType）
        if (row?.payment_type === PAYMENT_TYPE_ROYALTY) {
          const renderRoyalty = (v: unknown) => (
            <span>
              {formatPaymentAmount(v as number, PAYMENT_TYPE_ROYALTY, currency)}
            </span>
          );
          return renderDiffValue({
            rd: getRowDiff(row, activeDiffData),
            value,
            renderValue: renderRoyalty,
            diffKey: 'paymentAmount',
          });
        }
        // LF/MG 金额 → CurrencyType（对齐 BudgetOfferTable 金额展示；diff 旧值灰删除线）
        const rd = getRowDiff(row, activeDiffData);
        const renderNew = renderMoneyDiff(false);
        const renderOld = renderMoneyDiff(true);
        if (rd?.rowType === 'added') {
          return (
            <span className={DIFF_TEXT_CLASS.added}>{renderNew(value)}</span>
          );
        }
        if (rd?.rowType === 'deleted') {
          return renderOld(value);
        }
        if (rd?.paymentAmount) {
          return (
            <RoundDiffCell
              diff={rd.paymentAmount}
              renderNewValue={renderNew}
              renderOldValue={renderOld}
            />
          );
        }
        return renderNew(value);
      },
    };

    // 付款方式删除列（仅内包编辑态渲染；同节点仅剩 1 条时禁用）
    const typeOperationColumn: AddGroupFormColumn = {
      dataIndex: 'payment_item_operation',
      title: '操作',
      width: '60px',
      mergeRowSpanKey: ['item_key', 'payment_node_key', 'payment_type_key'],
      render: (value: any, row: PaymentPlanRow) => {
        const sameNodeRows = valueRef.current.filter(
          (r) => r.payment_node_key === row.payment_node_key && !r.$addRow
        ).length;
        return (
          <Button
            danger
            type="text"
            disabled={sameNodeRows <= 1}
            onClick={() => {
              tableRef.current?.treeDeleteRowById(
                row.payment_type_key as string
              );
            }}
          >
            删除
          </Button>
        );
      },
    };

    // 外层操作列（删除整个付款节点主行；仅内包编辑态渲染，每节点首个渲染行展示——含新增节点的占位子行）
    const nodeOperationColumn: AddGroupFormColumn = {
      dataIndex: 'payment_node_operation',
      title: '操作',
      width: '60px',
      mergeRowSpanKey: ['item_key', 'payment_node_key'],
      render: (value: any, row: PaymentPlanRow) => (
        <Button
          danger
          type="text"
          onClick={() => handleDeleteNode(row.payment_node_key)}
        >
          删除
        </Button>
      ),
    };

    // 付款方式（addGroup 子表：付款类型 + 付款金额 + 操作，每个付款节点下 1~N 条）
    const typeColumnGroup: AddGroupFormColumn = {
      title: '付款方式',
      addGroup: true,
      addGroupKey: ['item_key', 'payment_node_key'],
      children: [
        typeColumn,
        amountColumn,
      ],
    };
    if (isInternalEdit) {
      typeColumnGroup.addCellRender = renderAddTypeButton;
      typeColumnGroup.children.push(typeOperationColumn);
    }

    // 回复列（仅谈判视角：版权方操作 Tag 回显；外包 external 模式改 ActionCell 可编辑 + 整表跨行合并）
    const buildActionColumn = (): AddGroupFormColumn => {
      if (mode === 'external') {
        return {
          dataIndex: 'action',
          title: renderBatchEditTitle([PAYMENT_PLAN_ITEM_KEY]),
          // 外包编辑态回复列放 3 个 Medium Tag（对齐 CoreTermTable editable 宽）；外包只读单 Tag（100，对齐上线联动明细）
          width: isExternalEdit ? 180 : 100,
          mergeRowSpanKey: ['item_key'],
          editRender: ({ row }) => ({
            name: 'custom-component',
            // 回复列仅外包谈判场景可编辑（内包商务修改只读展示版权方回复）
            editable: isExternalEdit,
            props: {
              customComp: ActionCell,
              row,
              itemsByKey,
              editState,
              editable: isExternalEdit,
              onEditChange,
            },
          }),
          render: (value: any, row: PaymentPlanRow, index: number) => {
            // 只读态：ActionCell readOnly（整表合并，非首行返回 null）
            if (index !== 0) return null;
            return (
              <ActionCell
                row={row}
                itemsByKey={itemsByKey}
                editState={editState}
                editable={false}
                readOnly
                onEditChange={onEditChange}
              />
            );
          },
        };
      }
      return {
        dataIndex: 'action',
        title: '回复',
        width: isInternalEdit ? 130 : 100,
        mergeRowSpanKey: ['item_key'],
        render: (value: any, row: PaymentPlanRow, index: number) => {
          const rd = getRowDiff(row, activeDiffData);
          const action =
            row.action || itemsByKey[PAYMENT_PLAN_ITEM_KEY]?.action || '';
          if (rd?.action) {
            return <ActionDiffCell diff={rd.action} newAction={action} />;
          }
          if (rd?.rowType === 'deleted') {
            return <span className={OLD_VALUE_CLASS}>{action || '--'}</span>;
          }
          if (!action) return '--';
          return (
            <Tag
              size="small"
              text={ACTION_TEXT_MAP[action] || action || '-'}
              color={ACTION_TAG_COLOR[action] || 'light-grey'}
            />
          );
        },
      };
    };

    // 意见反馈/说明列（仅谈判视角：只读文本，diff 态新旧对比；外包 external 模式改 OpinionCell 可编辑 + 整表跨行合并）
    const buildOpinionColumn = (): AddGroupFormColumn => {
      if (mode === 'external') {
        return {
          dataIndex: 'opinion',
          title: getOpinionTitle(),
          // 外包：不设固定宽（弹性列吃剩余，回复列 x 对齐）；最小宽 100 防坍缩（对齐上线联动明细）
          minWidth: 100,
          mergeRowSpanKey: ['item_key'],
          editRender: ({ row }) => ({
            name: 'custom-component',
            // 意见反馈列仅外包谈判场景可编辑（内包商务修改只读展示）
            editable: isExternalEdit,
            props: {
              customComp: OpinionCell,
              row,
              itemsByKey,
              editState,
              editable: isExternalEdit,
              onEditChange,
            },
          }),
          render: (value: any, row: PaymentPlanRow, index: number) => {
            // 只读态：OpinionCell readOnly（整表合并，非首行返回 null）
            if (index !== 0) return null;
            return (
              <OpinionCell
                row={row}
                itemsByKey={itemsByKey}
                editState={editState}
                editable={false}
                readOnly
                onEditChange={onEditChange}
              />
            );
          },
        };
      }
      return {
        dataIndex: 'opinion',
        title: getOpinionTitle(),
        // 内包保持固定 400
        width: 400,
        mergeRowSpanKey: ['item_key'],
        render: (value: any, row: PaymentPlanRow, index: number) => {
          const rd = getRowDiff(row, activeDiffData);
          const opinion =
            row.opinion || itemsByKey[PAYMENT_PLAN_ITEM_KEY]?.opinion || '';
          const renderOpinion = (v: unknown) => (
            <span className="whitespace-pre-line">
              {String(v ?? '') || '--'}
            </span>
          );
          if (rd?.rowType === 'deleted') {
            return (
              <span className={`${OLD_VALUE_CLASS} whitespace-pre-line`}>
                {opinion || '--'}
              </span>
            );
          }
          if (rd?.opinion) {
            return (
              <RoundDiffCell
                diff={rd.opinion}
                renderNewValue={renderOpinion}
                renderOldValue={renderOpinion}
              />
            );
          }
          return renderOpinion(opinion);
        },
      };
    };

    // ===== 列组装 =====
    const columns = useMemo<AddGroupFormColumn[]>(
      () => {
        // 付款计划 root 列（addGroup 主表：序号 + 付款节点 + 付款方式子表）
        const rootColumn: AddGroupFormColumn = {
          dataIndex: 'item_key',
          title: null,
          addGroup: true,
          addGroupKey: ['item_key'],
          children: [indexColumn, nodeColumn, typeColumnGroup],
        };
        // 对比态不提供新增按钮（diff 只读展示）
        if (isInternalEdit && !activeDiffData) {
          rootColumn.addCellRender = renderAddNodeButton;
        }
        if (isInternalEdit) {
          rootColumn.children.push(nodeOperationColumn);
        }
        const cols: AddGroupFormColumn[] = [
          {
            dataIndex: 'payment_node_key',
            title: null,
            hideInTable: true,
            mergeRowSpanKey: ['item_key'],
          },
          {
            dataIndex: 'payment_type_key',
            title: null,
            hideInTable: true,
            mergeRowSpanKey: ['item_key'],
          },
          rootColumn,
        ];
        if (showNegotiationColumns) {
          cols.push(buildActionColumn(), buildOpinionColumn());
        }
        return cols;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        mode,
        isInternalEdit,
        isExternalEdit,
        showNegotiationColumns,
        editState,
        onEditChange,
        onBatchAction,
        basePaymentTypeOptions,
        paymentNodeOptions,
        activeDiffData,
        itemsByKey,
        currency,
      ]
    );

    return (
      <ProTablePlusForm
        ref={tableRef}
        value={value}
        onDataChange={onChange}
        columns={columns}
        rowKey={(r) => `${r.payment_node_key}-${r.payment_type_key}`}
        verticalBorder
        // 外包 external 模式：表格宽度随容器自适应（对齐 SolutionTable scroll x 100%）；内包保持 max-content
        scroll={{ x: mode === 'external' ? '100%' : 'max-content' }}
        readOnly={!editable}
      />
    );
  }
);

export default PaymentPlanTable;
